<template>
    <Card
        icon="fas fa-info-circle"
        bg-color="tw:bg-[var(--sitecolor)]"
        title-color="white"
        :title="'Información básica'"
        padding="0"
    >
        <div class="tw:p-6 tw:space-y-4">
            <!-- SGA/LFP pickers: rendered inside "Información básica" per UX
                 request (row on desktop, column on mobile via the tw:grid
                 tw:grid-cols-1 tw:md:grid-cols-2 pattern below). LFP picker
                 needs the full evento object; SGA product picker mounts when
                 an SGA event is selected. -->
            <div v-if="showSgaLfpPickers" class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mb-0">
                <div>
                    <LfpEventPicker
                        :evento="localData"
                        :sga="isSgaTeatro"
                        @selected="opt => $emit('lfp-sga-selected', opt)"
                        @deselected="$emit('lfp-sga-deselected')"
                    />
                </div>
                <div v-if="localData.sga_id && !experienciaOculta">
                    <SgaProductPicker
                        :key="localData.sga_id"
                        :sga-product-id="localData.sga_product_id"
                        :sga-event-id="localData.sga_id"
                        @selected="opt => $emit('sga-product-selected', opt)"
                        @deselected="$emit('sga-product-deselected')"
                        @no-products="$emit('sga-no-products')"
                    />
                </div>
            </div>
            <div v-if="showSgaLfpPickers" class="tw:border-t tw:border-gray-100 tw:my-4"></div>

            <!-- AvanzaEsp: el vínculo con el partido solo aplica a teatros con
                 evo_enabled y a eventos que no sean una experiencia (una
                 experiencia no es un partido). Mismo gate que el formulario
                 legacy: `teatro.evo_enabled && !localData.experiencia`. -->
            <template v-if="evoEnabled && !experienciaOculta">
                <EvoEventPicker
                    :evo-match-id="localData.evo_match_id"
                    @selected="onEvoSelected"
                    @cleared="onEvoCleared"
                />
                <div v-if="!localData.id">
                    <ToggleSwitch
                        :value="localData.create_in_evo"
                        :disabled="!!localData.evo_match_id"
                        variant="primary"
                        id="create_in_evo"
                        @input="val => set(localData, 'create_in_evo', val)"
                    >
                        {{ 'Crear partido en AvanzaEsp' }}
                    </ToggleSwitch>
                </div>
                <div class="tw:border-t tw:border-gray-100 tw:my-4"></div>
            </template>

            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4">
                <div class="tw:md:col-span-2">
                    <BaseInputText
                        id="titulo"
                        name="titulo"
                        data-cy="titulo"
                        :label="'Título'"
                        :value="localData.name"
                        rules="required"
                        @input="val => localData.name = val"
                    />
                </div>
                <div>
                    <BaseInputText
                        id="artista"
                        name="subtitulo"
                        data-cy="artista"
                        :label="'Subtítulo'"
                        :value="localData.author"
                        rules="required"
                        @input="val => localData.author = val"
                    />
                </div>
            </div>

            <template v-if="manager.tipo == 1">
                <div class="tw:border-t tw:border-gray-100 tw:my-5"></div>
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4">
                    <BaseMultiselect
                        :label="'Equipo local'"
                        name="equipo_local"
                        :value="localData.equipo_local_id"
                        :options="clubes"
                        label-key="nombre"
                        value-key="id"
                        :multiple="false"
                        :loading="lookupsLoading"
                        :disabled="lookupsLoading"
                        :rules="sportsTrioRequired ? 'required' : ''"
                        @input="val => set(localData, 'equipo_local_id', val)"
                    />
                    <BaseMultiselect
                        :label="'Equipo visitante'"
                        name="equipo_visitante"
                        :value="localData.equipo_visitante_id"
                        :options="clubes"
                        label-key="nombre"
                        value-key="id"
                        :multiple="false"
                        :loading="lookupsLoading"
                        :disabled="lookupsLoading"
                        :rules="sportsTrioRequired ? 'required' : ''"
                        @input="val => set(localData, 'equipo_visitante_id', val)"
                    />
                    <BaseMultiselect
                        :label="'Competición'"
                        name="division"
                        :value="localData.division_id"
                        :options="divisiones"
                        label-key="nombre"
                        value-key="id"
                        :multiple="false"
                        :loading="lookupsLoading"
                        :disabled="lookupsLoading"
                        :rules="sportsTrioRequired ? 'required' : ''"
                        @input="val => set(localData, 'division_id', val)"
                    />
                </div>
                <div class="tw:grid">
                    <BaseInputNumber
                        id="jornada"
                        name="jornada"
                        :label="'Jornada'"
                        :value="localData.jornada"
                        :rules="(localData.division_id || evoRequiresSportsTrio) ? 'required|numeric' : 'numeric'"
                        :min="1"
                        @input="val => localData.jornada = val"
                    />
                </div>
            </template>

            <div class="tw:border-t tw:border-gray-100"></div>

            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                <BaseMultiselect
                    :label="'Estado'"
                    name="estado"
                    :value="localData.status"
                    :options="estados"
                    label-key="nombre"
                    value-key="id"
                    :multiple="false"
                    :allow-empty="false"
                    @input="val => localData.status = val"
                />
                <div v-if="tags?.length">
                    <BaseMultiselect
                        :label="'Etiquetas'"
                        name="Etiquetas"
                        :value="localData.tags_seleccionadas"
                        :options="tags"
                        label-key="name"
                        value-key="id"
                        :multiple="true"
                        @input="val => set(localData, 'tags_seleccionadas', val)"
                    />
                </div>
            </div>
        </div>
    </Card>
</template>

<script setup>
    import { computed, inject, ref, set } from 'vue';
    import store from '@/store';
    import Card from '@/modules/admin/ui/Card.vue';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import LfpEventPicker from '@/modules/admin/eventos/components/LfpEventPicker.vue';
    import SgaProductPicker from '@/modules/admin/eventos/components/SgaProductPicker.vue';
    import EvoEventPicker from '@/modules/admin/eventos/EvoEventPicker.vue';
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';

    const localData = inject('eventoFormData');

    // SGA/LFP pickers moved into this Card (UX request): same visibility
    // rules FormEvento previously applied. `teatro` comes from the global
    // store (same source useEventForm uses), so this component stays
    // presentational and needs no extra props.
    const teatro = computed(() => store.getters.getTeatro);
    const isSgaTeatro = computed(() => !!teatro.value?.sga);
    // `manager` in useEventForm is store.getters.getFooter (the footer
    // config holds the manager type/LFP flags), NOT teatro.manager.
    const isLfpTeatro = computed(() => !!store.getters.getFooter?.lfp);

    // AvanzaEsp. `evoEnabled` es el gate de visibilidad del picker (solo
    // existe para teatros con la integración activa). El error del select lo
    // pinta su propio BaseMultiselect a través de la regla `evoPartidoRequerido`
    // (en useEventForm), con el mismo contrato que el gate del submit — así el
    // select muestra borde rojo y mensaje como cualquier otro campo.
    const evoEnabled = computed(() => !!teatro.value?.evo_enabled);
    const experienciaOculta = computed(() => !!localData.experiencia);

    // El trío deportivo (equipo local / visitante / competición) y la jornada
    // son obligatorios en dos supuestos:
    //   1. El de siempre: en cuanto se rellena uno de los tres, los demás pasan
    //      a obligatorios (`sportsFieldsFilled`, definido en useEventForm).
    //   2. AvanzaEsp: en un teatro con `evo_enabled` y un evento que NO es una
    //      experiencia el partido es obligatorio, y sin los equipos, la
    //      competición y la jornada ese partido no se puede identificar — el
    //      backend los necesita para crearlo en AvanzaEsp. Por eso se exigen
    //      aunque el usuario no haya tocado ninguno.
    // Nota: esto solo aplica a teatros tipo 1 (deportes), que son los únicos
    // donde este trío se renderiza y los únicos donde `evo_enabled` puede
    // activarse. Si esa condición cambiara, una regla `required` sobre campos
    // ocultos bloquearía el submit sin salida.
    const evoRequiresSportsTrio = computed(() =>
        evoEnabled.value && !experienciaOculta.value
    );
    const sportsTrioRequired = computed(() =>
        sportsFieldsFilled.value || evoRequiresSportsTrio.value
    );

    // Exclusividad: vincular un partido descarta crear uno nuevo (el backend
    // no admite ambas cosas a la vez).
    function onEvoSelected(selectedOption) {
        localData.evo_match_id = selectedOption.id;
        localData.create_in_evo = false;
    }

    function onEvoCleared() {
        localData.evo_match_id = null;
    }
    const showSgaLfpPickers = computed(() => {
        // Experiencia and SGA/LFP event are mutually exclusive in BOTH
        // directions, so when experiencia is checked the pickers hide in
        // SGA *and* LFP theaters alike (state is already cleared by the
        // watcher — this is the visual half).
        if (experienciaOculta.value) return false;
        return isSgaTeatro.value || isLfpTeatro.value;
    });

    // Cross-field validation for the sports-only trio (equipo_local,
    // equipo_visitante, division/competicion). If ANY of the three is
    // filled, ALL become required. If NONE has info, NONE is required.
    // Pattern mirrors `jornada`'s dynamic rule below: a computed that flips
    // each BaseMultiselect's `rules` between `'required'` and `''`. With
    // `mode="eager"` in BaseMultiselect's ValidationProvider, picking one
    // field surfaces "Required" on the other two immediately, which is the
    // intended UX for sports tenants (manager.tipo == 1).
    //
    // `sportsFieldsFilled` itself lives in useEventForm so EventoScheduling
    // can reuse the exact same predicate to relax the image requirement —
    // one source of truth instead of two drifting copies. Defaulted to a
    // `false`-valued ref so the trio still validates when rendered outside
    // FormEvento (e.g. an isolated component test).
    const { sportsFieldsFilled = ref(false) } = inject('eventoFormValidation', {});
    // Mientras los lookups están en vuelo, los selects que dependen de ellos se
    // deshabilitan: antes se renderizaban vacíos y el usuario no podía
    // distinguir "cargando" de "no hay opciones" (regla del repo: loader +
    // skeleton, no sólo el estado de error).
    const { lookupsLoading = ref(false) } = inject('eventoFormLookups', {});

    defineEmits(['lfp-sga-selected', 'sga-product-selected', 'sga-no-products', 'lfp-sga-deselected', 'sga-product-deselected']);


    const props = defineProps({
        clubes: { type: Array, default: () => [] },
        divisiones: { type: Array, default: () => [] },
        estados: { type: Array, default: () => [] },
        tags: { type: Array, default: () => [] },
        manager: { type: Object, default: () => ({}) }
    });
</script>
